import { removeEntryCollectionCircularReferences } from 'lib/contentful';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { getAllMenuPage } from 'repos/menu';
import { getAllReleases } from 'repos/releases';
import { getReleasesPage } from 'repos/releasesPage';

import SubscribeCTABlock from 'components/SubscribeCTABlock';
import Layout from 'components/layout';
import RichTextRenderer from 'components/richTextRenderer';
import SEO from 'components/seo';

function getFormattedDate(date: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
  }).format(new Date(date));
}

export default function ReleasesPage({
  releasesPage,
  releases,
  locale,
  menuParams,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { subtitle, title, ctaText } = releasesPage.fields;

  return (
    <Layout menuParams={menuParams}>
      <SEO title={title} description={subtitle} />
      <div className="bg-gray-100">
        <div className="mx-auto w-full max-w-6xl px-6 py-32">
          <div className="grid border-b border-gray-300 pb-20 md:grid-cols-4">
            <div className="md:col-span-1" />
            <div className="flex flex-col space-y-6 md:col-span-3">
              <h1 className="text-r-5xl font-bold">{title}</h1>
              <p className="text-lg text-gray-500">{subtitle}</p>
            </div>
          </div>
          <div className="divide-y divide-gray-300">
            {releases.map((release) => (
              <div
                key={release.sys.id}
                className="grid py-20 md:grid-cols-4 md:px-5 xl:px-0"
              >
                <div className="sticky top-24 hidden self-start md:col-span-1 md:block">
                  <time
                    dateTime={release.fields.date}
                    className="text-gray-500 hover:text-gray-800"
                  >
                    {getFormattedDate(release.fields.date, locale)}
                  </time>
                </div>
                <div className="flex flex-col gap-6 md:col-span-3">
                  <time
                    dateTime={release.fields.date}
                    className="text-sm text-gray-500 md:hidden"
                  >
                    {getFormattedDate(release.fields.date, locale)}
                  </time>
                  <article>
                    <RichTextRenderer
                      document={release.fields.content}
                      className="sm:prose-lg"
                    />
                  </article>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <SubscribeCTABlock text={ctaText} />
    </Layout>
  );
}

export async function getStaticProps({
  locale,
  preview,
}: GetStaticPropsContext) {
  const releasesPage = await getReleasesPage({ locale, preview });
  const menuParams = await getAllMenuPage({ locale });

  const releases = await getAllReleases({ locale, preview });

  return {
    props: {
      releasesPage,
      releases: removeEntryCollectionCircularReferences(releases).items,
      locale,
      menuParams,
    },
  };
}
